import React, { useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { SpaceBetween } from '@cloudscape-design/components';
import { Link } from 'src/components/Link';
import { ClientSideTable } from 'src/components/ClientSideTable';
import { useApplications } from 'src/hooks/useApplications';
import { ApplicationModal } from 'src/components/ApplicationModal';
import { ApiContext } from 'src/context/apiContext';
import { AccountContext } from 'src/context/accountContext';
import { Application } from 'src/types/application';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';

export const ApplicationList = () => {
  const { accountNo } = useParams();
  const { data, isLoading, error } = useApplications({
    account: accountNo,
  });
  const { dispatch } = useContext(ApiContext);
  const { selectedAccount } = useContext(AccountContext);

  const [applicationModalVisible, setApplicationModalVisible] =
    useState<boolean>(false);
  const [applicationModalMode, setApplicationModalMode] = useState<
    'New' | 'Edit' | 'Delete'
  >('New');
  const [applicationModalValues, setApplicationModalValues] =
    useState<any>(null);

  const showApplicationModal = (
    mode: 'New' | 'Edit' | 'Delete',
    values: any,
  ) => {
    setApplicationModalValues(values);
    setApplicationModalMode(mode);
    setApplicationModalVisible(true);
  };

  // Pre-sort the data by updatedDate in descending order
  const sortedData = React.useMemo(() => {
    if (!data) return [];
    // sort by updatedDate
    return [...data].sort((a, b) => {
      const dateA = a.updatedDate
        ? new Date(a.updatedDate)
        : new Date(a.createdDate);
      const dateB = b.updatedDate
        ? new Date(b.updatedDate)
        : new Date(b.createdDate);
      return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
    });
  }, [data]);

  const setNewApplication = (application: Application) => {
    dispatch({
      type: 'setCurrentApplicationId',
      payload: application.id,
    });
    dispatch({
      type: 'setCurrentModelVersionId',
      payload: '',
    });
    dispatch({
      type: 'setCurrentModelArtifactId',
      payload: '',
    });
  };

  return (
    <div>
      <ClientSideTable
        variant="container"
        resizableColumns
        actions={
          <PermissionAwareButton
            onClick={() => {
              showApplicationModal('New', null);
            }}
            variant="primary"
            requiredPermission="ADMINISTER"
            actionDescription="create applications"
          >
            Create a New Application
          </PermissionAwareButton>
        }
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: (item: Application) => (
              <Link
                to={`/accounts/${selectedAccount?.id}/applications/${item.id}`}
                onClick={() => {
                  setNewApplication(item);
                }}
              >
                {item.name}
              </Link>
            ),
            sortingField: 'name',
            isRowHeader: true,
          },
          {
            id: 'description',
            header: 'Description',
            cell: (item: Application) => item.description,
            sortingField: 'description',
          },
          {
            id: 'id',
            header: 'Id',
            cell: (item: Application) => item.id,
            sortingField: 'id',
          },
          {
            id: 'updatedDate',
            header: 'Updated Date',
            cell: (item: Application) => {
              const date = new Date(parseInt(item.updatedDate) * 1000);
              return date.toUTCString();
            },
            sortingField: 'updatedDate',
          },
          {
            id: 'updatedBy',
            header: 'Updated By',
            cell: (item: Application) => item.updatedBy,
            sortingField: 'updatedBy',
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item: Application) => (
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <PermissionAwareButton
                  onClick={() => showApplicationModal('Edit', item)}
                  variant="normal"
                  wrapText={false}
                  requiredPermission="ADMINISTER"
                  actionDescription="edit applications"
                >
                  Edit
                </PermissionAwareButton>
                <PermissionAwareButton
                  onClick={() => showApplicationModal('Delete', item)}
                  variant="normal"
                  wrapText={false}
                  requiredPermission="ADMINISTER"
                  actionDescription="delete applications"
                >
                  Delete
                </PermissionAwareButton>
              </SpaceBetween>
            ),
          },
        ]}
        data={sortedData}
        title="Applications"
        error={error}
        loading={isLoading}
        sortColumn="updatedDate"
        isDescending={true}
      />
      {accountNo && (
        <ApplicationModal
          visible={applicationModalVisible}
          modalMode={applicationModalMode}
          values={applicationModalValues}
          onClose={() => {
            setApplicationModalVisible(false);
          }}
          account={accountNo}
        />
      )}
    </div>
  );
};
